"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { StoryblokComponentList } from "@trydig/storyblok";
import { type ReactNode, useState, useTransition } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/form/Checkbox";
import { Input } from "@/components/ui/form/Input";
import { Select } from "@/components/ui/form/Select";
import { Textarea } from "@/components/ui/form/Textarea";
import { useGlobal } from "@/features/global/GlobalContext";
import type { CustomerServiceStoryblok } from "@/generated/sb/sb-types";
import { useTranslations } from "@/i18n/client";
import { trackGenerateLead } from "@/lib/gtm";
import { type CustomerServiceResult, submitCustomerServiceForm } from "./actions";
import { type CustomerServiceInput, customerServiceSchema } from "./schema";

export interface CustomerServiceFormTopic {
	value: string;
	label: string;
}

// Per-story Storyblok content fields. When set they override the
// `customerService.*` translation defaults; when empty the translation is used.
export interface CustomerServiceFormContent {
	emailLabel?: string;
	emailPlaceholder?: string;
	topicLabel?: string;
	topicPlaceholder?: string;
	messageLabel?: string;
	messagePlaceholder?: string;
	submitLabel?: string;
}

export interface CustomerServiceFormProps {
	topics: CustomerServiceStoryblok["topics"];

	consent?: ReactNode;
	content?: CustomerServiceFormContent;
}

export const CustomerServiceForm = ({ topics, consent, content }: CustomerServiceFormProps) => {
	const { preview } = useGlobal();
	const t = useTranslations("customerService");
	// tRaw resolves the fully-qualified keys returned by the schema / server
	// action (e.g. "customerService.fieldError.emailInvalid"). It's only called
	// with a variable at runtime, so the static references below are what
	// push-translations.ts picks up:
	//
	//   tRaw("customerService.fieldError.emailRequired")
	//   tRaw("customerService.fieldError.emailInvalid")
	//   tRaw("customerService.fieldError.topicRequired")
	//   tRaw("customerService.fieldError.messageRequired")
	//   tRaw("customerService.fieldError.consentRequired")
	const tRaw = useTranslations();
	const [alert, setAlert] = useState<CustomerServiceResult | null>(null);
	const [isPending, startTransition] = useTransition();

	const {
		control,
		handleSubmit,
		reset,
		setError,
		formState: { errors },
	} = useForm<CustomerServiceInput>({
		resolver: zodResolver(customerServiceSchema),
		defaultValues: {
			email: "",
			topic: "",
			message: "",
		},
	});

	// Schema / action error messages are fully-qualified translation keys;
	// tRaw resolves them (returning the string unchanged if already resolved).
	const resolveError = (key: string | undefined) => (key ? tRaw(key) : undefined);

	const onSubmit: SubmitHandler<CustomerServiceInput> = (values) => {
		setAlert(null);
		startTransition(async () => {
			const result = await submitCustomerServiceForm(values);

			if (!result.ok && result.reason === "validation") {
				for (const [field, key] of Object.entries(result.fieldErrors)) {
					if (key) setError(field as keyof CustomerServiceInput, { message: key });
				}
				return;
			}

			if (result.ok) {
				reset();
				trackGenerateLead({ form_name: "customer_service" });
			}
			setAlert(result);
		});
	};

	return (
		<form
			noValidate
			className="flex w-full flex-col items-start gap-grid-section-vertical-md"
			onSubmit={handleSubmit(onSubmit)}
		>
			<Controller
				control={control}
				name="email"
				render={({ field }) => (
					<Input
						type="email"
						autoComplete="email"
						label={content?.emailLabel}
						placeholder={content?.emailPlaceholder}
						name={field.name}
						value={field.value}
						onChange={field.onChange}
						onBlur={field.onBlur}
						ref={field.ref}
						disabled={isPending}
						isInvalid={Boolean(errors.email)}
						errorMessage={resolveError(errors.email?.message)}
						size="large"
					/>
				)}
			/>

			<Controller
				control={control}
				name="topic"
				render={({ field }) => (
					<Select
						label={content?.topicLabel}
						placeholder={content?.topicPlaceholder}
						selectedKey={field.value || null}
						onSelectionChange={(key) => field.onChange(key == null ? "" : String(key))}
						onBlur={field.onBlur}
						isDisabled={isPending}
						isInvalid={Boolean(errors.topic)}
						errorMessage={resolveError(errors.topic?.message)}
						size="large"
					>
						{topics && <StoryblokComponentList bloks={topics} preview={preview} />}
					</Select>
				)}
			/>

			<Controller
				control={control}
				name="message"
				render={({ field }) => (
					<Textarea
						label={content?.messageLabel}
						placeholder={content?.messagePlaceholder}
						name={field.name}
						value={field.value}
						onChange={field.onChange}
						onBlur={field.onBlur}
						isDisabled={isPending}
						isInvalid={Boolean(errors.message)}
						errorMessage={resolveError(errors.message?.message)}
						size="large"
					/>
				)}
			/>

			<div className="flex w-full flex-col items-start gap-grid-section-vertical-sm">
				<Controller
					control={control}
					name="consent"
					render={({ field }) => (
						<Checkbox
							size="large"
							isSelected={Boolean(field.value)}
							onChange={field.onChange}
							onBlur={field.onBlur}
							isInvalid={Boolean(errors.consent)}
							errorMessage={resolveError(errors.consent?.message)}
						>
							{consent}
						</Checkbox>
					)}
				/>

				<Button
					type="submit"
					variant="solid"
					color="primary"
					size="large"
					loading={isPending}
					className="w-full"
				>
					{content?.submitLabel}
				</Button>

				{alert?.ok && (
					<Alert
						variant="success"
						closable
						onClose={() => setAlert(null)}
						closeLabel={t("alert.closeLabel")}
						title={t("success.title")}
						className="w-full"
					/>
				)}
				{alert && !alert.ok && alert.reason === "server" && (
					<Alert
						variant="error"
						closable
						onClose={() => setAlert(null)}
						closeLabel={t("alert.closeLabel")}
						title={t("error.title")}
						description={t("error.description")}
						className="w-full"
					/>
				)}
			</div>
		</form>
	);
};
